import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FileText, ShieldCheck, Info, Smartphone, Archive, Send, Trash2 } from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import FormBuilderTabs from "@/components/forms/FormBuilderTabs";
import { api, formatApiError } from "@/lib/api";

const PAGE_TYPE_META = {
  terms: { label: "Terms & Conditions", icon: FileText, color: "text-[#0B7E8C]" },
  about: { label: "About Us", icon: Info, color: "text-[#7C3AED]" },
  consent: { label: "Consent Form", icon: ShieldCheck, color: "text-[#10B981]" },
  custom: { label: "Custom Page", icon: Smartphone, color: "text-[#6B7280]" },
};

const STATUS_STYLE = {
  draft: "bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]",
  published: "bg-[#D1FAE5] text-[#065F46] border-[#10B98155]",
  archived: "bg-[#FEE2E2] text-[#991B1B] border-[#EF444455]",
};

export default function MobileAppForms() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [createDraft, setCreateDraft] = useState({ title_en: "", title_ar: "", page_type: "custom" });
  const [confirm, setConfirm] = useState({ open: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/mobile-app-pages/admin");
      setItems(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((p) => statusFilter === "all" || p.status === statusFilter);

  async function handleCreate() {
    if (!createDraft.title_en.trim()) { toast.error("English title is required."); return; }
    try {
      const r = await api.post("/mobile-app-pages/admin", createDraft);
      toast.success("Page created — opening editor");
      setCreateOpen(false);
      setCreateDraft({ title_en: "", title_ar: "", page_type: "custom" });
      navigate(`/forms/mobile-app/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function handlePublish(page) {
    try { await api.post(`/mobile-app-pages/admin/${page.id}/publish`); toast.success(`${page.title_en} published (v${(page.version || 0) + 1})`); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function handleArchive(page) {
    try { await api.post(`/mobile-app-pages/admin/${page.id}/archive`); toast.success("Page archived"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function handleDelete(page) {
    try { await api.delete(`/mobile-app-pages/admin/${page.id}`); toast.success("Page deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="space-y-6" data-testid="mobile-app-forms-page">
      <PageHeader
        title="Form Builder"
        subtitle="Control the content and consent flows that appear inside the patient mobile app — Terms & Conditions, About Us, Consent Forms and any freeform pages you need."
      />

      <FormBuilderTabs active="mobile" />

      <div className="hah-card p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="text-sm text-[#6B7280]">
            {loading ? "Loading…" : `${filtered.length} of ${items.length} pages`}
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px] h-9" data-testid="mobile-forms-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          className="bg-[#0B7E8C] hover:bg-[#128C97] text-white"
          onClick={() => setCreateOpen(true)}
          data-testid="mobile-forms-new-page"
        >
          <Plus className="w-4 h-4 mr-1.5" /> New page
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="mobile-forms-list">
        {filtered.map((p) => {
          const meta = PAGE_TYPE_META[p.page_type] || PAGE_TYPE_META.custom;
          const Icon = meta.icon;
          return (
            <div key={p.id} className="hah-card p-4 flex flex-col gap-3"
                 data-testid={`mobile-page-card-${p.slug}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2.5 min-w-0">
                  <div className={`w-9 h-9 rounded-lg bg-[#F9FAFB] flex items-center justify-center shrink-0 ${meta.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[15px] font-semibold text-[#111827] truncate">{p.title_en || p.slug}</div>
                    <div className="text-xs text-[#6B7280] truncate">{meta.label} · /{p.slug}</div>
                  </div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase font-bold ${STATUS_STYLE[p.status]}`}>
                  {p.status}
                </span>
              </div>
              <div className="text-xs text-[#6B7280] flex items-center gap-3">
                <span>v{p.version || 0}</span>
                {p.required_at_signup && (
                  <span className="px-1.5 py-0.5 rounded bg-[#FEF3C7] text-[#92400E] font-semibold">
                    Required at signup
                  </span>
                )}
                {p.is_preset && (
                  <span className="px-1.5 py-0.5 rounded bg-[#EEF2FF] text-[#4338CA] font-semibold">Preset</span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <Button size="sm" variant="outline" className="border-[#E5E7EB]"
                        onClick={() => navigate(`/forms/mobile-app/${p.id}`)}
                        data-testid={`mobile-page-edit-${p.slug}`}>
                  Edit
                </Button>
                {p.status !== "published" && (
                  <Button size="sm" className="bg-[#10B981] hover:bg-[#059669] text-white"
                          onClick={() => handlePublish(p)}
                          data-testid={`mobile-page-publish-${p.slug}`}>
                    <Send className="w-3.5 h-3.5 mr-1" /> Publish
                  </Button>
                )}
                {p.status === "published" && !p.is_preset && (
                  <Button size="sm" variant="outline"
                          onClick={() => setConfirm({ open: true, action: "archive", page: p })}>
                    <Archive className="w-3.5 h-3.5 mr-1" /> Archive
                  </Button>
                )}
                {p.status === "archived" && !p.is_preset && (
                  <Button size="sm" variant="outline"
                          className="border-[#EF444433] text-[#EF4444]"
                          onClick={() => setConfirm({ open: true, action: "delete", page: p })}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                  </Button>
                )}
              </div>
            </div>
          );
        })}
        {!loading && filtered.length === 0 && (
          <div className="col-span-full hah-card p-8 text-center text-[#6B7280]">
            No pages match the current filter.
          </div>
        )}
      </div>

      {/* New page dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" data-testid="mobile-forms-new-dialog">
          <DialogHeader>
            <DialogTitle>New mobile app page</DialogTitle>
            <DialogDescription>
              Add a freeform page (Privacy Policy, FAQ, Refund Policy, extra consent…) that
              will appear inside the mobile app once published.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs font-semibold text-[#374151] block mb-1">Page type</label>
              <Select value={createDraft.page_type} onValueChange={(v) => setCreateDraft({ ...createDraft, page_type: v })}>
                <SelectTrigger data-testid="mobile-forms-new-page-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Custom (Markdown)</SelectItem>
                  <SelectItem value="consent">Consent (structured)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#374151] block mb-1">Title (English)</label>
              <Input value={createDraft.title_en} onChange={(e) => setCreateDraft({ ...createDraft, title_en: e.target.value })}
                     data-testid="mobile-forms-new-title-en" placeholder="e.g. Privacy Policy" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#374151] block mb-1">Title (Arabic)</label>
              <Input value={createDraft.title_ar} onChange={(e) => setCreateDraft({ ...createDraft, title_ar: e.target.value })}
                     data-testid="mobile-forms-new-title-ar" placeholder="مثال: سياسة الخصوصية" dir="rtl" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" onClick={handleCreate} data-testid="mobile-forms-new-submit">
              Create page
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirm.open}
        onOpenChange={(o) => setConfirm({ ...confirm, open: o })}
        title={confirm.action === "delete" ? "Delete page?" : "Archive page?"}
        description={confirm.action === "delete"
          ? "This permanently removes the archived page. It cannot be recovered."
          : "The page will be hidden from the mobile app until re-published."}
        confirmLabel={confirm.action === "delete" ? "Delete" : "Archive"}
        onConfirm={() => {
          const p = confirm.page;
          setConfirm({ open: false });
          if (confirm.action === "delete") handleDelete(p);
          else handleArchive(p);
        }}
      />
    </div>
  );
}
